import { getNestedValue } from '@common/utils/getNestedValue';
import { createContext, ReactNode, useCallback, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
    CollectionMetadata,
    DEFAULT_METADATA,
    MetadataSources,
    SourceOption,
} from '../types';
import { mergeWithDefault } from '@common/utils';

interface MetadataEditorContextValue {
    mergedData: CollectionMetadata;
    sources: MetadataSources;
    getSourceOptions: <T>(name: string) => SourceOption<T>[];
}

const MetadataEditorContext = createContext<MetadataEditorContextValue | null>(
    null,
);

// eslint-disable-next-line react-refresh/only-export-components
export const useMetadataEditorContext = () => {
    const context = useContext(MetadataEditorContext);
    if (!context) throw new Error('Must be used within MetadataEditorProvider');
    return context;
};

export const MetadataEditorProvider = ({
    mergedData,
    children,
    sources,
}: {
    mergedData: CollectionMetadata;
    children: ReactNode;
    sources: MetadataSources;
}) => {
    const methods = useForm<CollectionMetadata>({
        defaultValues: mergeWithDefault(mergedData, DEFAULT_METADATA),
    });
    const getSourceOptions = useCallback(
        <T,>(name: string): SourceOption<T>[] => {
            const defaultOverride: SourceOption<T> = {
                key: 'user_overrides',
                value: '' as T,
            };

            if (!sources) return [defaultOverride];

            const dbOptions = (['proscope', 'viva', 'tmdb'] as const).flatMap(
                (key): SourceOption<T>[] => {
                    const sourceData = sources[key];
                    const value = getNestedValue(sourceData, name) as T;
                    return [{ key, value }];
                },
            );

            return [defaultOverride, ...dbOptions];
        },
        [sources],
    );

    return (
        <FormProvider {...methods}>
            <MetadataEditorContext.Provider
                value={{ mergedData, sources, getSourceOptions }}
            >
                {children}
            </MetadataEditorContext.Provider>
        </FormProvider>
    );
};
